import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal, Signal, ViewChild, viewChildren, WritableSignal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { AgGridFilterModelInterface, ApplyGridFilterEvent, Filter, FilterOption, FilterPreference, getFilterValues } from '../extended-table.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { ConfigurationManagerService, ToolbarEventsService } from 'src/app/core';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { ConfirmDialogService } from 'src/app/dialogs';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { subscribeInComponent } from "src/app/shared/angular-utils";
import { TranslateModule } from '@ngx-translate/core';

export type CustomFiltersInputOperation = 'reset' | 'resetOnlyFilters' | 'finishSavingFilter' | ''
export type CustomFiltersOutputOperation = 'applyFilters' | 'applySavedFilter' | 
  'startSavingFilter' | 'toggleSidenav' | 'reset' | 'resetFilteredResult' | ''

export interface ParamsInputOperationsCustomFilter {
  operation: CustomFiltersInputOperation,
  extras?: any
}

export interface ParamsOutputOperationsCustomFilter {
  operation: CustomFiltersOutputOperation,
  extras?: any
}

interface FilterOptionUI {
  option: FilterOption;
  realIndex: number;
}

@Component({
  selector: 'app-custom-filters',
  standalone: true,
  imports: [MatFormFieldModule,
    MatIconModule, CommonModule, 
    MatCheckboxModule, ReactiveFormsModule,
    MatSelectModule, MatExpansionModule,
    MatInputModule, MatButtonModule, TranslateModule
  ],
  templateUrl: './custom-filters.component.html',
  styleUrls: ['./custom-filters.component.scss', '../extended-table.component.scss']
})
export class CustomFiltersComponent implements OnInit, OnDestroy{

  private readonly fb = inject(FormBuilder);
  private readonly configurationManager= inject(ConfigurationManagerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
    
  private destroy$ = new Subject<void>();

  private _inputOperation: CustomFiltersInputOperation = '';
  @Input()
  set triggerOperation(value: ParamsInputOperationsCustomFilter) {
    this._inputOperation = value.operation;
    if(value.operation === 'reset') {
      this.resetFilter(value?.extras?.deepReset);
    } else  if(value.operation === 'finishSavingFilter') {
      this.finishSavingFilter(value.extras?.filter);
    } else if(value.operation === 'resetOnlyFilters') {
      this.resetFiltersFromExternal()
    }
  }
  get inputOperation(): CustomFiltersInputOperation  {
    return this._inputOperation;
  }

  @Input() customFilters: Filter[] = [];
  @Input() filterApplied: boolean = false;
  @Input() hideResetFilterIcon: boolean = false;
  @Input() filterStatusSaved: ApplyGridFilterEvent | null = null;

  @Output() outputOperation = new EventEmitter<ParamsOutputOperationsCustomFilter>();
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  filtersForm: FormGroup;
  savedFiltersControl = new FormControl('');
  filterNameControl = new FormControl('', [Validators.required]);
  filteredOptionsMap: Map<string, WritableSignal<any[]>> = new Map();

  savedFilters!: FilterPreference[];
  filterEditingSectionTitle: string = "EXTENDED_TABLE.TITLE.CREATE_NEW_FILTER";
  createMode: boolean = true;
  filterNameTmp: string | null = '';
  
  panels: Signal<readonly MatExpansionPanel[]> = viewChildren(MatExpansionPanel);

  closeAllPanels(): void {
    this.panels().forEach(p => p.close());
  }

  constructor() {
    this.filtersForm = this.fb.group({});
    this.getSavedFilters();

    subscribeInComponent(this.toolbarEventsService.events, (event) => {
      switch(event.type) {
        case 'toolbar.clear_external_filter':
          return this.resetFilter(true);
        default:
          break;
      }
    });
  }

  ngOnInit(): void {
    this.buildForm();
    
    if (this.filterStatusSaved) {
      this.restoreSavedState();
    }
  }

  private buildForm(): void {
    this.customFilters.forEach(filter => {
      const filterGroup = this.fb.group({
        search: [''],
        options: this.fb.array(filter.options.map((option, idx) => this.fb.group({
          id: option.id || null,
          value: option.name || null,
          selected: false,
          originalIndex: idx
        })))
      });

      this.filtersForm.addControl(filter.field, filterGroup);
      this.updateFilteredOptions(filter.field);

      this.filtersForm.addControl('filterName', this.filterNameControl);
    });
  }

  private restoreSavedState(): void {
    if (!this.filterStatusSaved || this.filterStatusSaved.activeFilterType !== 'customFilters') {
      return;
    }

    const { agGridFilters, customFilterNameSelected } = this.filterStatusSaved;

    if (customFilterNameSelected) {
      this.savedFiltersControl.setValue(customFilterNameSelected);
      this.filterNameControl.setValue(customFilterNameSelected);
      this.filterNameTmp = customFilterNameSelected;
      this.updateUseMode(false);
    }

    if (agGridFilters) {
      Object.keys(agGridFilters).forEach(field => {
        const savedIds = getFilterValues(agGridFilters, field);

        if (savedIds && Array.isArray(savedIds)) {
          const optionsArray = this.getOptionsFormArray(field);
          
          if (optionsArray) {
            optionsArray.controls.forEach((control) => {
              const optionId = control.get('id')?.value;
              
              const isSelected = savedIds.some(id => String(id) === String(optionId));
              
              if (isSelected) {
                control.get('selected')?.setValue(true);
              }
            });
          }
          this.updateFilteredOptions(field);
        }
      });
    }
  }

  onSearchInput(field: string, panel: MatExpansionPanel) {
    this.updateFilteredOptions(field);
    this.scrollToPanel(panel);
  }

  private scrollToPanel(panel: any) {
    setTimeout(() => {
      const panelElement = panel._body.nativeElement;
      const container = this.scrollContainer.nativeElement;
      const targetScroll = panelElement.offsetTop;

      container.scrollTo({
          top: targetScroll, 
          behavior: 'smooth' 
      });
    })
  }

  checkboxSelectionChange(event: MatCheckboxChange, field: string) {
    this.updateFilteredOptions(field);
    this.outputOperation.emit({
      operation: 'applyFilters', 
      extras: { formValue: this.filtersForm.value, filterName: this.filterNameControl.value}
    });
  }

  private updateFilteredOptions(field: string): void {
    const filter = this.customFilters.find(f => f.field === field);
    const searchTerm = this.filtersForm.get([field, 'search'])?.value?.toLowerCase() || '';
    const optionsArray = this.getOptionsFormArray(field).value;

    if (!filter) return;

    const processed: FilterOptionUI[] = optionsArray
      .map((optValue: any, index: number) => ({
        option: filter.options.find(o => o.id? o.id === optValue.id: o.name === optValue.value)!,
        realIndex: index,
        selected: optValue.selected,
        originalOrder: optValue.originalIndex
      }))
      .filter((item: any) => {
        const matchesSearch = item.option.name.toLowerCase().includes(searchTerm);
        return matchesSearch || item.selected;
      })
      .sort((a: any, b: any) => {
        if (a.selected !== b.selected) return a.selected ? -1 : 0;
        return a.originalOrder - b.originalOrder;
      });

    if (!this.filteredOptionsMap.has(field)) {
      this.filteredOptionsMap.set(field, signal(processed));
    } else {
      this.filteredOptionsMap.get(field)?.set(processed);
    }
  }


  initFields(filter: FilterPreference) {
    for(let filterName in filter.filterValues) {
      const setFilter = getFilterValues(filter.filterValues, filterName);
      if(setFilter?.length) {
        setFilter.forEach((filterId: number) => {
          this.getOptionsFormArray(filterName)?.controls.find(opt => opt.value.id == filterId)?.get('selected')?.setValue(true, {emitEvent: false});
        });
        this.updateFilteredOptions(filterName);
      }
      this.filterNameControl.setValue(filter.name);
      this.filterNameTmp = filter.name;
    }
  }

  getSavedFilters() {
    this.savedFilters = this.parseFiltersPreference();
  }

  getOptionsFormArray(filterName: string): FormArray {
    return this.filtersForm.get([filterName, 'options']) as FormArray;
  }

  getOptionFormGroup(filterName: string, index: number): FormGroup {
    return (this.filtersForm.get([filterName, 'options']) as FormArray).at(index) as FormGroup;
  }

  getSelectionSummary(filterName: string): string {
    const selectedOptions = this.getOptionsFormArray(filterName)?.value.filter(
      (opt: {selected:boolean}) => opt.selected)

    if (!selectedOptions || selectedOptions.length === 0) {
      return 'Vuoto';
    }
    if (selectedOptions.length > 1) {
      return `${selectedOptions.length} valori`;
    }
    return selectedOptions
      .map((opt: {value: string}) => opt.value)
      .join(', ');
  }

  toggleSidenav() {
    this.outputOperation.emit({operation: "toggleSidenav"})
  }

  onSelectionChange() {
    this.resetFilter();
    this.updateFilterResult();
  }

  resetFiltersFromExternal() {
    this.resetOnlyCustomFilter();
    this.pristineInitialContent();
  }

  resetFilter(deepReset = false): void {
    this.resetOnlyCustomFilter();

    this.outputOperation.emit({operation: "reset", extras: {deepReset: deepReset}})

    if(deepReset) {
      this.pristineInitialContent();
    }
  }

  resetOnlyCustomFilter() {
    if (this.filtersForm) {
      this.customFilters.forEach(filter => {
        const filterGroup = this.filtersForm.get(filter.field);
        
        if (filterGroup) {
          filterGroup.get('search')?.setValue('', { emitEvent: false });

          this.getOptionsFormArray(filter.field)?.controls.forEach((optionControl) => {
            if (optionControl.get('selected')?.value) {
              optionControl.get('selected')?.setValue(false, { emitEvent: false });
            }
          });

          this.updateFilteredOptions(filter.field);
        }
      });
    }

    this.filterNameControl.setValue('');
    this.filterNameControl.markAsUntouched();
    this.filterNameTmp = "";
    this.closeAllPanels();
  }

  clearSearchField(filterName: string) {
    this.filtersForm.get([filterName, 'search'])?.setValue(''); 
    this.updateFilteredOptions(filterName);
  }

  pristineInitialContent() {
    if(this.savedFiltersControl.value) this.savedFiltersControl.setValue("");
    this.updateUseMode(true);
  }

  updateFilterResult(updateOnlyIcon = false) {
    this.updateUseMode(this.savedFiltersControl.value == null);
    const filter = this.savedFiltersControl.value? this.savedFilters.filter(f => f.name == this.savedFiltersControl.value): null;
    if(filter) {
      this.initFields(filter[0])
    }
    this.outputOperation.emit({operation: "applySavedFilter", extras: {filter: filter, updateOnlyIcon: updateOnlyIcon}})
  }

  updateUseMode(createMode: boolean) {
    if(createMode) {
      this.createMode = true;
      this.filterEditingSectionTitle = "EXTENDED_TABLE.TITLE.CREATE_NEW_FILTER";
    } else {
      this.createMode = false;
      this.filterEditingSectionTitle = "EXTENDED_TABLE.TITLE.FILTER_APPLIED";
    }
  }

  parseFiltersPreference() {
    if(this.configurationManager.getPreference('grid.items.filters')) {
      const startFilters = this.configurationManager.getPreference('grid.items.filters');
      return JSON.parse(startFilters || "");
    }
    return [];
  }

  deleteFilter(): void {
    let filters = this.parseFiltersPreference();

    filters = this.updateFilterPreferences(filters, {name: this.savedFiltersControl.value}, false);

    const stringify = JSON.stringify(filters)

    this.configurationManager.setUserPreference('grid.items.filters', stringify);
    this.getSavedFilters();

    this.resetFilter(true);
  }

  async openDeleteDialog() {
    const res = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'DIALOG.ITEMS_FILTERS.TITLE',
        confirmLabel: 'DIALOG.CONFIRM.YES',
        cancelLabel: 'DIALOG.CONFIRM.NO'
      }
    });

    if(res) {
      this.deleteFilter();
    }
  }

  saveFilter() {
    this.outputOperation.emit({operation: 'startSavingFilter', extras: {formValue: this.filtersForm.value}})
  }

  finishSavingFilter(filterToSave: AgGridFilterModelInterface) {
    if(Object.keys(filterToSave).length) {

      const newFilter = {
        name: this.filterNameControl.value?.trim(),
        filterValues: filterToSave
      }

      let filters = this.parseFiltersPreference();

      if(this.filterNameTmp && this.filterNameTmp !== newFilter.name) {
        filters = this.updateFilterPreferences(filters, {name: this.filterNameTmp}, false);
      } 
      
      filters = this.updateFilterPreferences(filters, newFilter);

      const stringify = JSON.stringify(filters)

      this.configurationManager.setUserPreference('grid.items.filters', stringify);
      this.getSavedFilters();

      this.savedFiltersControl.setValue(this.filterNameControl.value);
      this.filterNameTmp = this.filterNameControl.value;
      this.closeAllPanels();
      this.updateFilterResult();
    }
  }

  updateFilterPreferences(startArray:any, filter: any, add = true): Array<Object> {
    
    const index = startArray?.length? startArray.findIndex((item: { name: string; }) => item.name === filter.name): -1;
    if (index !== -1) {
      if(add) {
        startArray[index] = filter;
      } else {
        startArray.splice(index, 1);
      }
    } else if(add) {
      startArray.push(filter);
    }
    return startArray;
  }

  /** Restituisce il numero di opzioni selezionate per un determinato campo */
  getSelectedCount(filterName: string): number {
    const options = this.getOptionsFormArray(filterName)?.value;
    const result = options ? options.filter((opt: any) => opt.selected).length : 0;
    return result;
  }

  scrollToTarget(event: FocusEvent) {
    const element = event.target as HTMLElement;
    setTimeout(() => {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }, 250);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
